/** @jsxImportSource preact */
import { h } from 'preact';
import { Modal } from './Modal';

export interface DocumentListItem {
  documentId: string;
  officialName: string;
}

export interface ActiveFilter {
  label: string;
  values: string[];
}

interface DocumentListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: DocumentListItem[];
  loading: boolean;
  onDocumentClick: (documentId: string) => void;
  activeFilters?: ActiveFilter[];
}

export function DocumentListModal({
  isOpen,
  onClose,
  title,
  items,
  loading,
  onDocumentClick,
  activeFilters,
}: DocumentListModalProps) {
  const handleDocumentClick = (e: MouseEvent, documentId: string) => {
    e.preventDefault();
    onDocumentClick(documentId);
  };

  const hasFilters = activeFilters && activeFilters.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {hasFilters && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          paddingBottom: 12,
          marginBottom: 4,
          borderBottom: '1px solid var(--gc-border, #e5e7eb)',
        }}>
          {activeFilters.map((filter) => (
            <span
              key={filter.label}
              style={{
                display: 'inline-block',
                fontSize: 11,
                padding: '3px 8px',
                borderRadius: 12,
                backgroundColor: 'var(--gc-badge-bg, #f3f4f6)',
                color: 'var(--gc-badge-text, #374151)',
                border: '1px solid var(--gc-border, #e5e7eb)',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontWeight: 600 }}>{filter.label}:</span>{' '}
              {filter.values.join(', ')}
            </span>
          ))}
        </div>
      )}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          Loading documents...
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          No documents found.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  padding: '6px 0',
                  borderBottom: '1px solid var(--gc-border, #e5e7eb)',
                  width: '20%',
                  fontWeight: 600,
                }}
              >
                Document ID
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '6px 0',
                  borderBottom: '1px solid var(--gc-border, #e5e7eb)',
                  fontWeight: 600,
                }}
              >
                Official Name
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.documentId}-${index}`}>
                <td style={{ padding: '6px 0', verticalAlign: 'top' }}>
                  {item.documentId ? (
                    <a
                      href="#"
                      onClick={(e) => handleDocumentClick(e as unknown as MouseEvent, item.documentId)}
                      style={{ color: 'var(--gc-link, inherit)', textDecoration: 'none' }}
                    >
                      {item.documentId}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td style={{ padding: '6px 0' }}>
                  {item.documentId ? (
                    <a
                      href="#"
                      onClick={(e) => handleDocumentClick(e as unknown as MouseEvent, item.documentId)}
                      style={{ color: 'var(--gc-link, inherit)', textDecoration: 'none' }}
                    >
                      {item.officialName || item.documentId}
                    </a>
                  ) : (
                    item.officialName || '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
